
import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Minus, ShoppingCart } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface SmartRecommendationsProps {
  onAddToCart: (quantity: number) => void;
}

interface Product {
  id: number;
  name: string;
  image: string;
  brand: string;
  category: string;
  packSize: string;
  suggestedQty: number;
  currentStock: number;
  unitPrice: number;
  tag?: string;
}

const SmartRecommendations: React.FC<SmartRecommendationsProps> = ({ onAddToCart }) => {
  // Sample data - in a real app this would come from an API
  const recommendedProducts: Product[] = [
    {
      id: 1,
      name: "Fresh Milk",
      image: "/placeholder.svg",
      brand: "Farm Fresh",
      category: "Dairy",
      packSize: "1L",
      suggestedQty: 10,
      currentStock: 2,
      unitPrice: 6.90,
      tag: "Running low"
    },
    {
      id: 2,
      name: "White Bread",
      image: "/placeholder.svg",
      brand: "Gardenia",
      category: "Bakery",
      packSize: "400g",
      suggestedQty: 15,
      currentStock: 3,
      unitPrice: 3.50,
      tag: "Fast-moving"
    },
    {
      id: 3,
      name: "Eggs",
      image: "/placeholder.svg",
      brand: "Farm Best",
      category: "Dairy",
      packSize: "10pcs",
      suggestedQty: 8,
      currentStock: 1,
      unitPrice: 5.80,
      tag: "Running low"
    },
    {
      id: 4,
      name: "Milo",
      image: "/placeholder.svg",
      brand: "Nestlé",
      category: "Beverages",
      packSize: "1kg",
      suggestedQty: 5,
      currentStock: 2,
      unitPrice: 23.90,
      tag: "Promo"
    },
    {
      id: 5,
      name: "Cooking Oil",
      image: "/placeholder.svg",
      brand: "Knife",
      category: "Cooking Essentials",
      packSize: "5kg",
      suggestedQty: 6,
      currentStock: 3,
      unitPrice: 28.90,
      tag: "Fast-moving"
    }
  ];

  const lowStockProducts = recommendedProducts.filter(p => p.currentStock <= 2);
  const fastMovingProducts = recommendedProducts.filter(p => p.tag === "Fast-moving");
  const promotionProducts = recommendedProducts.filter(p => p.tag === "Promo");

  const [quantities, setQuantities] = useState<Record<number, number>>(
    recommendedProducts.reduce((acc, product) => {
      acc[product.id] = product.suggestedQty;
      return acc;
    }, {} as Record<number, number>)
  );

  const handleQuantityChange = (id: number, increment: boolean) => {
    setQuantities(prev => ({
      ...prev,
      [id]: increment ? prev[id] + 1 : Math.max(0, prev[id] - 1)
    }));
  };

  const handleAddToCart = (productId: number) => {
    const quantity = quantities[productId];
    if (quantity > 0) {
      onAddToCart(quantity);
      // Reset quantity after adding to cart
      setQuantities(prev => ({
        ...prev,
        [productId]: 0
      }));
    }
  };

  const renderProductTable = (products: Product[]) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-green-50">
            <TableHead className="w-[80px]">Image</TableHead>
            <TableHead>Product</TableHead>
            <TableHead>Brand</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Pack Size</TableHead>
            <TableHead>Suggested Qty</TableHead>
            <TableHead>Current Stock</TableHead>
            <TableHead>Unit Price (RM)</TableHead>
            <TableHead>Quantity</TableHead>
            <TableHead>Action</TableHead>
            <TableHead>Note</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product) => (
            <TableRow key={product.id}>
              <TableCell>
                <img 
                  src={product.image} 
                  alt={product.name} 
                  className="w-12 h-12 object-cover rounded-md" 
                />
              </TableCell>
              <TableCell className="font-medium">{product.name}</TableCell>
              <TableCell>{product.brand}</TableCell>
              <TableCell>{product.category}</TableCell>
              <TableCell>{product.packSize}</TableCell>
              <TableCell>{product.suggestedQty}</TableCell>
              <TableCell>{product.currentStock}</TableCell>
              <TableCell>{product.unitPrice.toFixed(2)}</TableCell>
              <TableCell>
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => handleQuantityChange(product.id, false)}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-8 text-center">{quantities[product.id]}</span>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => handleQuantityChange(product.id, true)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
              <TableCell>
                <Button 
                  size="sm" 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handleAddToCart(product.id)}
                  disabled={quantities[product.id] === 0}
                >
                  <ShoppingCart className="mr-2 h-4 w-4" />
                  Add to Cart
                </Button>
              </TableCell>
              <TableCell>
                {product.tag && (
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    product.tag === 'Running low' ? 'bg-red-100 text-red-800' : 
                    product.tag === 'Fast-moving' ? 'bg-blue-100 text-blue-800' : 
                    'bg-amber-100 text-amber-800'
                  }`}>
                    {product.tag}
                  </span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">AI-Generated Purchase Suggestions</h3>
      
      <Tabs defaultValue="recommended" className="w-full">
        <TabsList className="bg-green-50">
          <TabsTrigger value="recommended">Recommended Products</TabsTrigger>
          <TabsTrigger value="low-stock">Low Stock Alerts</TabsTrigger>
          <TabsTrigger value="browse-all">Browse All</TabsTrigger>
          <TabsTrigger value="fast-moving">Fast-Moving</TabsTrigger>
          <TabsTrigger value="promotions">Promotions</TabsTrigger>
          <TabsTrigger value="subsidy">Subsidy Eligible</TabsTrigger>
        </TabsList>
        
        <Card className="mt-4 border-green-100">
          <CardContent className="p-4">
            <TabsContent value="recommended" className="mt-0">
              {renderProductTable(recommendedProducts)}
            </TabsContent>
            
            <TabsContent value="low-stock" className="mt-0">
              {renderProductTable(lowStockProducts)}
            </TabsContent>
            
            <TabsContent value="browse-all" className="mt-0">
              {renderProductTable(recommendedProducts)}
            </TabsContent>
            
            <TabsContent value="fast-moving" className="mt-0">
              {renderProductTable(fastMovingProducts)}
            </TabsContent>
            
            <TabsContent value="promotions" className="mt-0">
              {renderProductTable(promotionProducts)}
            </TabsContent>
            
            <TabsContent value="subsidy" className="mt-0">
              <p className="text-center py-8 text-muted-foreground">No subsidy eligible products available at this time.</p>
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
};

export default SmartRecommendations;
